import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import './SignupPage.css';

type Step = 'form' | 'verify';

function SignupPage() {
  const { user, loading, sendOtpCode, verifyOtpCode } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('form');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (!loading && user) {
      const pendingToken = localStorage.getItem('pending_invite_token');
      if (pendingToken) {
        localStorage.removeItem('pending_invite_token');
        navigate(`/invite/${pendingToken}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    setSubmitting(true);
    const hasPendingInvite = !!localStorage.getItem('pending_invite_token');
    const { error: err } = await sendOtpCode(email.trim(), {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      company: company.trim() || undefined,
      role: role.trim() || undefined,
      accountType: hasPendingInvite ? 'member' : 'team_leader',
      mode: 'signup',
    });
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setStep('verify');
    setResendCountdown(60);
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!code.trim() || code.trim().length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setSubmitting(true);
    const { error: err } = await verifyOtpCode(email.trim(), code.trim());
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
  }

  async function handleResend() {
    setError(null);
    setSubmitting(true);
    const hasPendingInvite = !!localStorage.getItem('pending_invite_token');
    const { error: err } = await sendOtpCode(email.trim(), {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      company: company.trim() || undefined,
      role: role.trim() || undefined,
      accountType: hasPendingInvite ? 'member' : 'team_leader',
      mode: 'signup',
    });
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setResendCountdown(60);
    setCode('');
  }

  if (loading) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <div className="signup-card">
            <div className="animate-pulse text-center">
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-info">
          <h1>Create a Team Account</h1>
          <p className="signup-intro">
            Accounts are for team leaders who want to invite others and view aggregated insights.
          </p>

          <div className="signup-note-box">
            <span className="note-icon">💡</span>
            <div>
              <strong>Just want to take the assessment?</strong>
              <p>
                No account needed!{' '}
                <Link to="/assessment">Start your assessment</Link> right away—it's completely free.
              </p>
            </div>
          </div>

          <div className="signup-benefits">
            <div className="benefit-item">
              <div className="benefit-icon">👥</div>
              <div>
                <strong>Team Management</strong>
                <span>Create teams and invite members</span>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">📊</div>
              <div>
                <strong>Team Insights</strong>
                <span>View aggregated team analytics</span>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">📈</div>
              <div>
                <strong>Track Progress</strong>
                <span>Monitor leadership development</span>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">💾</div>
              <div>
                <strong>Save Results</strong>
                <span>Access your assessments anytime</span>
              </div>
            </div>
          </div>
        </div>

        <div className="signup-form-container">
          <div className="signup-card">
            {step === 'form' ? (
              <>
                <h2>Enter Your Details</h2>
                <p className="form-subtitle">
                  Your information is kept confidential and used only for your account.
                </p>

                <form className="signup-form" onSubmit={handleSendCode}>
                  <div className="form-group">
                    <label htmlFor="email">EMAIL ADDRESS *</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={submitting}
                      autoComplete="email"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">FIRST NAME *</label>
                      <input
                        id="firstName"
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={submitting}
                        autoComplete="given-name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">LAST NAME *</label>
                      <input
                        id="lastName"
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={submitting}
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">COMPANY (OPTIONAL)</label>
                    <input
                      id="company"
                      type="text"
                      placeholder="Your organization"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      disabled={submitting}
                      autoComplete="organization"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="role">JOB TITLE (OPTIONAL)</label>
                    <input
                      id="role"
                      type="text"
                      placeholder="Your job title"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      disabled={submitting}
                      autoComplete="organization-title"
                    />
                  </div>

                  {error && <p className="form-error">{error}</p>}

                  <button
                    type="submit"
                    className="btn btn-primary submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending...' : 'Send Verification Code'}
                  </button>

                  <p className="form-note">
                    We'll send you a 6-digit code to verify your email—no password needed.
                  </p>

                  <p className="form-privacy">
                    By creating an account, you agree to our privacy policy and terms of service.
                  </p>
                </form>

                <div className="signup-divider">
                  <span>Already have an account?</span>
                </div>

                <Link to="/login" className="btn btn-secondary login-link">
                  Sign In
                </Link>
              </>
            ) : (
              <>
                <div className="email-sent-icon">📧</div>
                <h2 style={{ textAlign: 'center' }}>Check Your Email</h2>
                <p className="signup-subtitle">
                  We sent a 6-digit code to <strong>{email}</strong>. Enter it below to verify your account.
                </p>

                <form className="signup-form" onSubmit={handleVerifyCode}>
                  <div className="form-group">
                    <label htmlFor="code">VERIFICATION CODE</label>
                    <input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={submitting}
                      className="otp-input"
                      autoFocus
                      maxLength={6}
                      required
                    />
                  </div>

                  {error && <p className="form-error">{error}</p>}

                  <button
                    type="submit"
                    className="btn btn-primary submit-btn"
                    disabled={submitting || code.length !== 6}
                  >
                    {submitting ? 'Verifying...' : 'Verify Code'}
                  </button>

                  <p className="signup-note">
                    {resendCountdown > 0 ? (
                      <>Resend available in {resendCountdown}s</>
                    ) : (
                      <>
                        Didn't receive it?{' '}
                        <button
                          type="button"
                          className="link-button"
                          onClick={handleResend}
                          disabled={submitting}
                        >
                          Resend code
                        </button>
                      </>
                    )}
                  </p>
                </form>

                <button
                  type="button"
                  className="back-link"
                  onClick={() => { setStep('form'); setError(null); setCode(''); }}
                >
                  ← Go back
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
